import { getAuthToken } from '../services/authService';

/**
 * מודל היכולות (Capabilities) של הפרונטאנד — מקור-אמת יחיד להרשאות תצוגה.
 *
 * ⚠️ תצוגה בלבד: הבקנד הוא גבול האבטחה היחיד (policies ב-P2b). כאן רק מחליטים
 * מה להציג/להסתיר ולאן לנתב. המודל נגזר מה-claims החדשים ב-JWT (`ml_*`) ומשקף
 * אחד-לאחד את `PermissionInvariants` בבקנד.
 */

export type Role = 'Manager' | 'Employee' | 'Agent';

/** שמות היכולות זהים במכוון לשמות ה-policies בבקנד (`AuthorizationPolicies.cs`). */
export type Capability =
  | 'IsManager'
  | 'IsEmployee'
  | 'IsAgent'
  | 'IsStaff'
  | 'CanManageSettings'
  | 'CanViewClientFinance'
  | 'CanViewFinancialReports';

export interface Capabilities {
  role: Role | null;
  isManager: boolean;
  isEmployee: boolean;
  isAgent: boolean;
  /** Manager || Employee */
  isStaff: boolean;
  /** Manager || flag  (invariant: Manager ⇒ true) */
  canViewClientFinance: boolean;
  /** Manager && flag */
  canViewFinancialReports: boolean;
  /** Manager */
  canManageSettings: boolean;
  departmentIds: number[];
}

const ROLE_LABELS_HE: Record<Role, string> = {
  Manager: 'מנהל',
  Employee: 'עובד',
  Agent: 'סוכן',
};

/** מפענח את גוף ה-JWT מה-cookie. ה-claims המותאמים (`ml_*`) מופיעים מילולית ב-payload. */
function decodeTokenPayload(): Record<string, unknown> | null {
  const token = getAuthToken();
  if (!token) return null;
  try {
    const base64Payload = token.split('.')[1];
    return JSON.parse(atob(base64Payload));
  } catch {
    return null;
  }
}

/** משקף את `CurrentUserService.ParseBool` — C# `bool.ToString()` נותן "True"/"False" (רישיות ראשית). */
function parseBool(value: unknown): boolean {
  return typeof value === 'string' && value.toLowerCase() === 'true';
}

/** משקף את `CurrentUserService.ParseDepartmentIds` — CSV מופרד-פסיק. */
function parseDepartmentIds(value: unknown): number[] {
  if (typeof value !== 'string' || value.length === 0) return [];
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => Number(part))
    .filter((num) => !Number.isNaN(num));
}

/** מקבל את שם ה-enum ("Manager"/"Employee"/"Agent") ולחלופין ערך מספרי (1/2/3). */
function normalizeRole(value: unknown): Role | null {
  if (value === 'Manager' || value === 'Employee' || value === 'Agent') return value;
  if (value === '1' || value === 1) return 'Manager';
  if (value === '2' || value === 2) return 'Employee';
  if (value === '3' || value === 3) return 'Agent';
  return null;
}

/**
 * קורא את מודל היכולות מה-JWT.
 * מחזיר `null` כאשר `ml_role` חסר (טוקן ישן שהונפק לפני P2b) — אות לכפיית re-login נקי.
 */
export function readCapabilities(): Capabilities | null {
  const payload = decodeTokenPayload();
  if (!payload) return null;

  const role = normalizeRole(payload['ml_role']);
  if (!role) return null; // אין claims חדשים → טוקן ישן

  const clientFinanceFlag = parseBool(payload['ml_can_view_client_finance']);
  const financialReportsFlag = parseBool(payload['ml_can_view_financial_reports']);

  const isManager = role === 'Manager';
  const isEmployee = role === 'Employee';
  const isAgent = role === 'Agent';

  return {
    role,
    isManager,
    isEmployee,
    isAgent,
    isStaff: isManager || isEmployee,
    canViewClientFinance: isManager || clientFinanceFlag,
    canViewFinancialReports: isManager && financialReportsFlag,
    canManageSettings: isManager,
    departmentIds: parseDepartmentIds(payload['ml_department_ids']),
  };
}

/** בדיקת יכולת בודדת (מחזיר false כאשר אין caps). משמש את `ProtectedRoute` והגידור הדינמי. */
export function hasCapability(caps: Capabilities | null, capability: Capability): boolean {
  if (!caps) return false;
  switch (capability) {
    case 'IsManager':
      return caps.isManager;
    case 'IsEmployee':
      return caps.isEmployee;
    case 'IsAgent':
      return caps.isAgent;
    case 'IsStaff':
      return caps.isStaff;
    case 'CanManageSettings':
      return caps.canManageSettings;
    case 'CanViewClientFinance':
      return caps.canViewClientFinance;
    case 'CanViewFinancialReports':
      return caps.canViewFinancialReports;
    default:
      return false;
  }
}

/** תווית עברית לתפקיד — לתצוגה (footer וכד'). */
export function roleLabelHe(role: Role | null): string {
  return role ? ROLE_LABELS_HE[role] : '';
}
